import React, { FunctionComponent } from 'react';
import { Grid, Typography, Divider } from '@material-ui/core';
import clsx from 'clsx';
import LabelAndPrice from '../labels/LabelAndPrice';
import expandedSectionStyles from './ExpandedSectionStyles';
import { IExteriorColour, IInteriorColour } from '../../../../../../../../common/model/IColours';
import usePlatformConfig from '../../../../../../common/react/usePlatformConfig';
import ServicePlanSection from './Sections/ServicePlanSection';
import AccessoriesSection from './Sections/AccessoriesSection';
import ColoursSection from './Sections/ColoursSection';
import AccessoryPackSection from './Sections/AccessoryPackSection';
import useScrollToSection from '../../../../../react/navigation/useScrollToSection';
import ICarConfigOption from '../../../../../../../../common/model/ICarConfigOption';
import Translation from '../../../../../../common/components/translation/Translation';
import { useSelector } from 'react-redux';
import currentDealSelectors from '../../../../../store/selectors/currentDealSelectors';

export interface IExpandedSectionProps {
  exteriorColour: IExteriorColour;
  interiorColour: IInteriorColour;
  govChargesPrice?: number;
  servicePlan?: ICarConfigOption;
  accessories?: ICarConfigOption[];
  accessoryPacks?: ICarConfigOption[];
  removeAction: (category: string, optionId?: string) => void;
  currency: string;
  isSecondaryCurrencyDisplay: boolean;
}

const ExpandedSection: FunctionComponent<IExpandedSectionProps> = ({
  exteriorColour,
  interiorColour,
  servicePlan,
  govChargesPrice,
  accessories,
  accessoryPacks,
  removeAction,
  currency,
  isSecondaryCurrencyDisplay,
}) => {
  const styles = expandedSectionStyles();
  const {
    platformConfig: { showGovernmentCharges, priceDisplay },
  } = usePlatformConfig();
  const { scrollToSection, scrollToDisclaimer } = useScrollToSection();
  const isMultiCurrency = useSelector(currentDealSelectors.getIsMultiCurrency);

  return (
    <>
      <Grid item={true}>
        <Typography variant="h5">
          <Translation id={'spc.customise.selectionsTitle'} />
        </Typography>
      </Grid>
      <ColoursSection
        exteriorColour={exteriorColour}
        interiorColour={interiorColour}
        changeAction={scrollToSection}
        decimalScale={priceDisplay.primaryOptions.decimalScale}
        currency={currency}
      />
      {accessoryPacks?.length > 0 && (
        <>
          <Divider />
          <AccessoryPackSection
            packs={accessoryPacks}
            removeAction={removeAction}
            isSecondaryCurrencyDisplay={isMultiCurrency && isSecondaryCurrencyDisplay}
          />
        </>
      )}
      {accessories?.length > 0 && (
        <>
          <Divider />
          <AccessoriesSection
            accessories={accessories}
            removeAction={removeAction}
            isSecondaryCurrencyDisplay={isMultiCurrency && isSecondaryCurrencyDisplay}
            scrollToDisclaimer={scrollToDisclaimer}
          />
        </>
      )}
      {servicePlan?.prices && (
        <>
          <Divider />
          <ServicePlanSection servicePlan={servicePlan} removeAction={removeAction} />
        </>
      )}
      {showGovernmentCharges && govChargesPrice > 0 && (
        <>
          <Divider />
          <Grid item={true} data-test={'summary_expanded:section:govCharges'}>
            <Typography variant={'body1'} className={clsx([styles.bolded, styles.subtitle])}>
              <Translation id={'spc.customise.fees'} />
            </Typography>
            <LabelAndPrice
              label={<Translation id={'spc.customise.governmentCharges'} />}
              price={govChargesPrice}
              currency={'AUD'}
              priceType="fees"
              isMainText={false}
              decimals={priceDisplay.primaryOptions.decimalScale}
            />
          </Grid>
        </>
      )}
    </>
  );
};

export default ExpandedSection;
